import React, { useState, ReactNode } from 'react';
import { FormLabel, TextField } from '@mui/material';
import clsx from 'clsx';
import InputAdornment from '@mui/material/InputAdornment';
import CircularProgress from '@mui/material/CircularProgress';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import { withStyles, WithStyles } from '@mui/styles';
import { COLORS } from '~/themes/common';
import Tooltip from '~/components/common/Tooltip';

const style = (theme: any) => ({
  container: {
    display: 'flex',
    flex: 1,
    position: 'relative',
  },
  root: {
    'flex': 1,
    'width': '100%',

    '& input': {
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      fontSize: 14,
    },
    '& textarea': {
      fontSize: 14,
    },
    '& .MuiFormHelperText-root:first-letter': {
      textTransform: 'uppercase',
    },

    '& .MuiInputBase-root.Mui-disabled': {
      background: COLORS.gray.disableGray2,
      pointerEvents: 'none',
    },
  },
  inputAdornment: {
    'background': COLORS.palette.secondaryBlue3,
    '&:hover': {
      backgroundColor: '#FFF',
      WebkitBoxShadow: '0 0 0 30px white inset !important',
    },
  },
  iconContainer: {
    cursor: 'pointer',
    display: 'flex',
    justifyContent: 'center',
    marginRight: 8,
  },
  tickIcon: {
    marginRight: 10,
  },
  processingIcon: {
    marginRight: 12,
  },
  tooltip: {
    marginLeft: 16,
    cursor: 'pointer',
    display: 'inline-block',
    paddingTop: 6,
  },
  hasLegend: {
    '& $tooltip': {
      paddingTop: 28,
    },
  },
  tooltipContent: {
    paddingRight: 36,
    display: 'block',
    textAlign: 'left',
    fontSize: 12,
  },
  tooltipContentPaddingLeft: {
    paddingLeft: 48,
  },
  legendTooltip: {
    marginLeft: 8,
  },
  leftItem: {
    marginRight: 16,
    height: 32,
    display: 'inline-flex',
    alignItems: 'center',
  },
  leftItemWithTopSpace: {
    marginTop: 22,
  },
  formLabel: {
    textAlign: 'left',
    fontSize: 14,
    letterSpacing: 0,
    color: COLORS.palette.black,
    marginBottom: theme.spacing(1),
    opacity: 1,
    position: 'relative',
    zIndex: 2,
    display: '-webkit-box',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    WebkitBoxOrient: 'vertical',
    WebkitLineClamp: 1,
  },
  main: {
    flex: 1,
    minWidth: 0,
    position: 'relative',
  },
  endDotDotDot: {
    '& input': {
      display: 'inline-block',
      whiteSpace: 'nowrap',
      overflow: 'hidden !important',
      textOverflow: 'ellipsis',
    },
  },
});

const TextInputStyled = withStyles((theme: any) => ({
  root: {
    '& input': {
      fontSize: 14,
    },
    '& .MuiOutlinedInput-root': {
      paddingRight: 0,
    },
    '&.correct': {
      '& .MuiOutlinedInput-root': {
        '& fieldset': {
          borderColor: COLORS.accent.viridianGreen1,
        },
        '&:hover fieldset': {
          borderColor: COLORS.accent.viridianGreen1,
        },
        '&.Mui-focused fieldset': {
          borderColor: COLORS.accent.viridianGreen1,
        },
      },
    },
    '&.remove-cursor': {
      '& .MuiOutlinedInput-root': {
        '& input': {
          'color': 'transparent',
          'textShadow': `0 0 0 ${COLORS.gray.textGray1}`,
          'cursor': 'pointer',
          '&::placeholder': {
            color: 'transparent',
          },
        },
      },
    },
    '& input.MuiInputBase-input': {
      'height': theme.spacing(2),
      'paddingTop': theme.spacing(1),
      'paddingBottom': theme.spacing(1),
      'paddingRight': theme.spacing(2),
      '&:disabled': {
        'borderRadius': theme.spacing(0.5),
        'background': COLORS.gray.disableGray2,
        'cursor': 'auto !important',
        '-webkit-text-fill-color': '#2E2E2E !important',
      },
    },
    '& .MuiOutlinedInput-root.Mui-disabled': {
      background: COLORS.gray.disableGray2,
    },

    '& .MuiFormHelperText-contained': {
      marginRight: 3,
      marginLeft: 3,
    },

    '& .MuiFormHelperText-root.Mui-error': {
      color: COLORS.accent.rubyRed1,
    },

    '&.leftItem': {
      '& .MuiFormHelperText-contained': {
        marginRight: 3,
      },
    },

    '& .MuiInputLabel-outlined': {
      [theme.breakpoints.up('lg')]: {
        'fontSize': 14,

        '&:not(.MuiInputLabel-shrink)': {
          transform: 'translate(14px, 7px) scale(1) !important',
        },
      },

      '&:not(.MuiInputLabel-shrink)': {
        transform: 'translate(14px, 7px) scale(1) !important',
      },
    },
  },
}))(TextField);

interface TooltipConfig {
  responsive?: boolean;
  size?: string;
  icon?: ReactNode;
  content?: ReactNode;
  [key: string]: any;
}

interface TextInputProps extends WithStyles<typeof style> {
  className?: string;
  containerClassName?: string;
  type?: string;
  correct?: boolean;
  processing?: boolean;
  tooltip?: TooltipConfig | null;
  leftItem?: ReactNode;
  rightIcon?: boolean;
  legend?: string | ReactNode;
  legendTooltip?: Record<string, any> | null;
  offsetEnd?: boolean;
  error?: boolean;
  helperText?: string;
  endDotDotDot?: boolean;
  disabledScroll?: boolean;
  handleBlur?: () => void;
  inputProps?: Record<string, any>;
  checkRemoveCursor?: boolean;
  legendStyle?: string;
  hasValue?: boolean;
  [key: string]: any;
}

const TextInput: React.FC<TextInputProps> = ({
  classes,
  className = '',
  containerClassName = '',
  type = '',
  correct = false,
  processing = false,
  tooltip = null,
  leftItem = null,
  rightIcon = false,
  legend = '',
  legendTooltip = null,
  offsetEnd = false,
  error = false,
  helperText = '',
  endDotDotDot = false,
  disabledScroll = false,
  handleBlur = () => {},
  inputProps = {},
  checkRemoveCursor = false,
  legendStyle = '',
  hasValue = false,
  ...props
}) => {
  const [show, setShow] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const passwordClasses = type === 'password' ? ' password' : '';
  const correctClasses = correct ? 'correct' : '';
  const removeCursorClasses = checkRemoveCursor ? 'remove-cursor' : '';

  const theme = useTheme();
  const isTablet = useMediaQuery(theme.breakpoints.down('sm'));

  const renderTooltip = () => {
    if (isTablet && tooltip?.responsive) {
      return (
        <div
          role='presentation'
          className={classes.tooltip}
          style={{ width: tooltip?.size || 'auto' }}
          onClick={() => setShowTooltip(!showTooltip)}
        >
          {tooltip?.icon || <img src='/icons/info.svg' alt='info' />}
        </div>
      );
    }

    const { responsive, ...tooltipProps } = tooltip || {};
    return <Tooltip className={classes.tooltip} {...tooltipProps} />;
  };

  const [passwordHasValue, setPasswordHasValue] = useState(false);

  return (
    <>
      <div
        className={clsx(classes.container, containerClassName, {
          [classes.endDotDotDot]: endDotDotDot,
          [classes.hasLegend]: !!legend,
        })}
      >
        {leftItem && (
          <div
            className={clsx(classes.leftItem, {
              [classes.leftItemWithTopSpace]: legend,
            })}
          >
            {leftItem}
          </div>
        )}

        <div className={classes.main}>
          {legend && (
            <FormLabel
              component='legend'
              className={clsx(classes.formLabel, legendStyle)}
            >
              {legend}
              {legendTooltip && (
                <Tooltip className={classes.legendTooltip} {...legendTooltip} />
              )}
            </FormLabel>
          )}

          <TextInputStyled
            className={clsx(
              passwordClasses,
              correctClasses,
              removeCursorClasses,
              className,
              classes.root,
            )}
            variant='outlined'
            size='small'
            type={show ? 'text' : type}
            error={error}
            helperText={helperText}
            onWheel={
              disabledScroll ? (event: any) => event.target.blur() : undefined
            }
            onAnimationStart={(e: React.AnimationEvent<HTMLInputElement>) => {
              const autoFilled = !!(e.target as HTMLInputElement)?.matches('*:-webkit-autofill');
              if (
                e.animationName === 'mui-auto-fill' ||
                e.animationName === 'mui-auto-fill-cancel'
              ) {
                setPasswordHasValue(autoFilled);
              }
            }}
            InputLabelProps={{
              shrink: passwordHasValue || hasValue,
            }}
            InputProps={{
              endAdornment: rightIcon && (
                <InputAdornment
                  position='end'
                  className={classes.inputAdornment}
                >
                  {correct && (
                    <div
                      className={clsx(classes.iconContainer, classes.tickIcon)}
                    >
                      <img alt='tick' src='/icons/tick-input.svg' />
                    </div>
                  )}
                  {type === 'password' && (
                    <div
                      className={classes.iconContainer}
                      role='presentation'
                      onClick={() => setShow(!show)}
                    >
                      {show ? (
                        <img alt='visible' src='/icons/visibility.svg' />
                      ) : (
                        <img alt='invisible' src='/icons/visibility-off.svg' />
                      )}
                    </div>
                  )}
                  {processing && (
                    <div
                      className={clsx(
                        classes.iconContainer,
                        classes.processingIcon,
                      )}
                    >
                      <CircularProgress size={16} />
                    </div>
                  )}
                </InputAdornment>
              ),
            }}
            {...props}
            // eslint-disable-next-line react/jsx-no-duplicate-props
            inputProps={{
              ...inputProps,
            }}
            {...inputProps}
          />
        </div>
        {tooltip && renderTooltip()}
        {offsetEnd && <span style={{ width: 36 }} />}
      </div>
      {isTablet && showTooltip && tooltip?.responsive && tooltip?.content && (
        <div
          className={clsx(classes.tooltipContent, {
            [classes.tooltipContentPaddingLeft]: leftItem,
          })}
        >
          {tooltip?.content}
        </div>
      )}
    </>
  );
};

export default withStyles(style)(TextInput);
